import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/layout/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { SCOPE_META, SCOPE_ORDER } from "@/lib/discovery";
import { toast } from "sonner";
import { Users, LayoutTemplate, HelpCircle, GitBranch, BookOpen, ChevronDown, Plus, Pencil, Trash2, Layers, UserPlus, KeyRound } from "lucide-react";
import { cn } from "@/lib/utils";

const ROLE_TONE = { admin: "violet", consultant: "blue", customer: "green" };
const TID = "tmpl-sales-cloud-core";
const TYPE_LABELS = {
  yes_no: "Yes / No", single_select: "Single select", multi_select: "Multi select",
  short_text: "Short text", long_text: "Long text", number: "Number", currency: "Currency",
  date: "Date", dropdown: "Dropdown", table: "Table", tags: "Tags", file_upload: "File upload", hierarchy: "Hierarchy",
};
const EDITABLE_TYPES = ["yes_no", "single_select", "multi_select", "short_text", "long_text", "number", "date", "dropdown", "table", "tags", "hierarchy"];

export default function Administration() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);
  const [template, setTemplate] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [editor, setEditor] = useState(null); // { module, question|null }
  const [userDialog, setUserDialog] = useState(false);

  const reloadUsers = () => api.get("/users").then((r) => setUsers(r.data));
  const loadTemplate = () => api.get(`/templates/${TID}`).then((r) => setTemplate(r.data)).catch(() => {});
  useEffect(() => {
    reloadUsers();
    loadTemplate();
  }, []);

  const resetPassword = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}/password`, {});
      toast.success(`New password for ${u.email}: ${data.temp_password}`, { duration: 12000 });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const deleteUser = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); reloadUsers(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const removeQuestion = async (mkey, qkey) => {
    try {
      await api.delete(`/templates/${TID}/modules/${mkey}/questions/${qkey}`);
      toast.success("Question deleted");
      loadTemplate();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <PageHeader title="Administration" subtitle="Manage users and the configurable discovery library." />
      <div className="flex-1 px-6 py-8 lg:px-10">
        <Tabs defaultValue="modules">
          <TabsList className="mb-6 bg-slate-100">
            <TabsTrigger value="users" data-testid="admin-tab-users"><Users className="mr-1.5 h-4 w-4" /> Users</TabsTrigger>
            <TabsTrigger value="modules" data-testid="admin-tab-modules"><LayoutTemplate className="mr-1.5 h-4 w-4" /> Modules & Questions</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            {isAdmin && (
              <div className="mb-4 flex justify-end">
                <Button data-testid="add-user-btn" onClick={() => setUserDialog(true)} className="rounded-lg bg-sky-600 hover:bg-sky-700"><UserPlus className="mr-1.5 h-4 w-4" /> Add User</Button>
              </div>
            )}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <tr><th className="px-6 py-3">Name</th><th className="px-6 py-3">Email</th><th className="px-6 py-3">Role</th>{isAdmin && <th className="px-6 py-3 text-right">Actions</th>}</tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t border-slate-100">
                      <td className="px-6 py-4 font-semibold text-slate-800">{u.name}</td>
                      <td className="px-6 py-4 text-slate-600">{u.email}</td>
                      <td className="px-6 py-4"><StatusBadge label={u.role} tone={ROLE_TONE[u.role]} /></td>
                      {isAdmin && (
                        <td className="px-6 py-4">
                          <div className="flex justify-end gap-1">
                            <button data-testid={`reset-pw-${u.email}`} title="Reset password" onClick={() => resetPassword(u)} className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-sky-600"><KeyRound className="h-4 w-4" /></button>
                            <button data-testid={`delete-user-${u.email}`} title="Delete user" onClick={() => deleteUser(u)} className="rounded p-1.5 text-slate-400 hover:bg-slate-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="modules">
            <div className="mb-4 flex items-center gap-2">
              <h3 className="font-heading text-lg font-bold text-slate-900">{template?.name}</h3>
              <span className="rounded-md bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">metadata-driven</span>
              {!isAdmin && <span className="text-xs text-slate-400">· read-only (admins can edit)</span>}
            </div>
            <div className="space-y-3">
              {template?.modules.map((m) => (
                <div key={m.key} className="rounded-xl border border-slate-200 bg-white">
                  <button data-testid={`admin-module-${m.key}`} onClick={() => setOpenModule(openModule === m.key ? null : m.key)}
                    className="flex w-full items-center justify-between p-4 text-left">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-xs font-bold text-white">{m.code}</span>
                      <div>
                        <div className="font-heading text-sm font-bold text-slate-800">{m.title}</div>
                        <div className="text-xs text-slate-400">{m.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-semibold text-slate-500">
                      <span className="flex items-center gap-1"><HelpCircle className="h-3.5 w-3.5" /> {m.questions.length}</span>
                      <span className="flex items-center gap-1"><GitBranch className="h-3.5 w-3.5" /> {m.questions.filter((q) => q.conditional).length}</span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", openModule === m.key && "rotate-180")} />
                    </div>
                  </button>

                  {openModule === m.key && (
                    <div className="border-t border-slate-100 p-4">
                      {isAdmin && (
                        <div className="mb-3 flex justify-end">
                          <Button size="sm" variant="outline" data-testid={`add-question-${m.key}`} onClick={() => setEditor({ module: m.key, question: null })} className="rounded-lg">
                            <Plus className="mr-1 h-4 w-4" /> Add question
                          </Button>
                        </div>
                      )}
                      <div className="space-y-2">
                        {m.questions.map((q) => (
                          <div key={q.key} data-testid={`admin-question-${q.key}`} className="flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-slate-800">{q.text}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-medium text-slate-400">
                                <span className="rounded bg-white px-1.5 py-0.5 ring-1 ring-slate-200">{TYPE_LABELS[q.type] || q.type}</span>
                                <span>{q.section}</span>
                                {q.min_scope && <span className="flex items-center gap-0.5 text-sky-500"><Layers className="h-3 w-3" /> {SCOPE_META[q.min_scope]?.label || q.min_scope}</span>}
                                {q.required && <span className="text-red-400">required</span>}
                                {q.conditional && <span className="flex items-center gap-0.5 text-amber-500"><GitBranch className="h-3 w-3" /> conditional</span>}
                                {q.options?.length > 0 && <span>{q.options.length} options</span>}
                              </div>
                            </div>
                            {isAdmin && (
                              <div className="flex shrink-0 gap-1">
                                <button data-testid={`edit-question-${q.key}`} onClick={() => setEditor({ module: m.key, question: q })} className="rounded p-1.5 text-slate-400 hover:bg-white hover:text-sky-600"><Pencil className="h-4 w-4" /></button>
                                <button data-testid={`delete-question-${q.key}`} onClick={() => removeQuestion(m.key, q.key)} className="rounded p-1.5 text-slate-400 hover:bg-white hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {editor && <QuestionEditor tid={TID} module={editor.module} question={editor.question} onClose={() => setEditor(null)} onSaved={() => { setEditor(null); loadTemplate(); }} />}
      {userDialog && <AddUserDialog onClose={() => setUserDialog(false)} onCreated={reloadUsers} />}
    </>
  );
}

function AddUserDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("customer");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const save = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/users", { name: name.trim(), email: email.trim(), role, password: password.trim() || null });
      setCreated(data);
      toast.success("User created");
      onCreated();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add a user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-sm font-semibold">Name</Label><Input data-testid="au-name" value={name} onChange={(e) => setName(e.target.value)} disabled={!!created} className="mt-1 rounded-lg" /></div>
          <div><Label className="text-sm font-semibold">Email</Label><Input data-testid="au-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!created} className="mt-1 rounded-lg" /></div>
          <div><Label className="text-sm font-semibold">Role</Label>
            <Select value={role} onValueChange={setRole} disabled={!!created}>
              <SelectTrigger data-testid="au-role" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="consultant">Consultant</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!created && <div><Label className="text-sm font-semibold">Password (optional)</Label><Input data-testid="au-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-lg" placeholder="Leave blank to auto-generate" /></div>}
          {created && (
            <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800" data-testid="au-result">
              Account created for <strong>{created.email}</strong>.{created.temp_password ? <> Temporary password: <strong>{created.temp_password}</strong> — share this with the user.</> : " Password set as entered."}
            </div>
          )}
        </div>
        <DialogFooter>
          {created ? (
            <Button data-testid="au-done" onClick={onClose} className="rounded-lg bg-sky-600 hover:bg-sky-700">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
              <Button data-testid="au-save" onClick={save} disabled={busy} className="rounded-lg bg-sky-600 hover:bg-sky-700">{busy ? "Creating…" : "Create user"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuestionEditor({ tid, module, question, onClose, onSaved }) {
  const editing = !!question;
  const [text, setText] = useState(question?.text || "");
  const [section, setSection] = useState(question?.section || "General");
  const [type, setType] = useState(question?.type || "long_text");
  const [help, setHelp] = useState(question?.help_text || "");
  const [example, setExample] = useState(question?.example || "");
  const [required, setRequired] = useState(question?.required || false);
  const [minScope, setMinScope] = useState(question?.min_scope || "standard");
  const [optionsText, setOptionsText] = useState((question?.options || []).map((o) => o.label).join("\n"));
  const [optionRows, setOptionRows] = useState((question?.options || []).map((o) => ({ label: o.label, scope: o.scope || "standard" })));
  const [busy, setBusy] = useState(false);
  const scopeable = ["single_select", "multi_select", "dropdown"].includes(type);
  const textOptions = ["table", "tags"].includes(type);

  const updateRow = (i, field, val) => setOptionRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const addRow = () => setOptionRows((rows) => [...rows, { label: "", scope: "standard" }]);
  const removeRow = (i) => setOptionRows((rows) => rows.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!text.trim()) { toast.error("Question text is required"); return; }
    setBusy(true);
    try {
      let options = null;
      if (scopeable) options = optionRows.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), scope: r.scope }));
      else if (textOptions) options = optionsText.split("\n").map((s) => s.trim()).filter(Boolean);
      const body = { text, section, type, help_text: help, example, required, options, min_scope: minScope };
      if (editing) await api.put(`/templates/${tid}/modules/${module}/questions/${question.key}`, body);
      else await api.post(`/templates/${tid}/modules/${module}/questions`, body);
      toast.success(editing ? "Question updated" : "Question added");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit question" : "Add question"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-sm font-semibold">Question text</Label><Textarea data-testid="qe-text" value={text} onChange={(e) => setText(e.target.value)} className="mt-1 rounded-lg" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-semibold">Section</Label><Input data-testid="qe-section" value={section} onChange={(e) => setSection(e.target.value)} className="mt-1 rounded-lg" /></div>
            <div><Label className="text-sm font-semibold">Answer type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="qe-type" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{EDITABLE_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-sm font-semibold"><Layers className="h-3.5 w-3.5 text-sky-500" /> Minimum scope to ask this question</Label>
            <Select value={minScope} onValueChange={setMinScope}>
              <SelectTrigger data-testid="qe-min-scope" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}</SelectContent>
            </Select>
            <p className="mt-1 text-xs text-slate-400">The question is shown only when the module's scope is at least this level.</p>
          </div>
          <div><Label className="text-sm font-semibold">Help text ("Why are we asking this?")</Label><Textarea data-testid="qe-help" value={help} onChange={(e) => setHelp(e.target.value)} className="mt-1 rounded-lg" /></div>
          <div><Label className="text-sm font-semibold">Example (optional)</Label><Input data-testid="qe-example" value={example} onChange={(e) => setExample(e.target.value)} className="mt-1 rounded-lg" /></div>
          {scopeable && (
            <div>
              <Label className="text-sm font-semibold">Options & scope</Label>
              <p className="text-xs text-slate-400">Tag an option with a deeper scope to auto-flag a potential scope expansion when a customer selects it.</p>
              <div className="mt-2 space-y-2">
                {optionRows.map((r, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`qe-opt-label-${i}`} value={r.label} onChange={(e) => updateRow(i, "label", e.target.value)} placeholder="Option label" className="rounded-lg" />
                    <Select value={r.scope} onValueChange={(v) => updateRow(i, "scope", v)}>
                      <SelectTrigger data-testid={`qe-opt-scope-${i}`} className="w-44 shrink-0 rounded-lg text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}</SelectContent>
                    </Select>
                    <button type="button" onClick={() => removeRow(i)} className="shrink-0 rounded p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" data-testid="qe-add-option" onClick={addRow} className="rounded-lg"><Plus className="mr-1 h-3.5 w-3.5" /> Add option</Button>
              </div>
            </div>
          )}
          {textOptions && <div><Label className="text-sm font-semibold">Options / columns (one per line)</Label><Textarea data-testid="qe-options" value={optionsText} onChange={(e) => setOptionsText(e.target.value)} className="mt-1 min-h-[110px] rounded-lg" placeholder={"Website\nEmail\nPhone"} /></div>}
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Switch data-testid="qe-required" checked={required} onCheckedChange={setRequired} /> Required question</label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button data-testid="qe-save" onClick={save} disabled={busy} className="rounded-lg bg-sky-600 hover:bg-sky-700">{busy ? "Saving…" : editing ? "Save changes" : "Add question"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
